import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import useAutoLogout from '../../hooks/useAutoLogout';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SIDEBAR_OPEN_WIDTH = 256; // 64 * 4 (w-64)
const SIDEBAR_COLLAPSED_WIDTH = 80; // 20 * 4 (w-20)

const LayoutWrapper = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Start closed on mobile
  const [sidebarVisible, setSidebarVisible] = useState(true); // New state for complete visibility
  const [isMobile, setIsMobile] = useState(false);
  useAutoLogout();

  const handleSidebarToggle = useCallback(() => {
    if (sidebarVisible) {
      // If sidebar is visible, hide it completely
      setSidebarVisible(false);
      setSidebarOpen(false);
    } else {
      // If sidebar is hidden, show it and open it
      setSidebarVisible(true);
      setSidebarOpen(true);
    }
  }, [sidebarVisible]);

  const handleSidebarClose = useCallback(() => {
    setSidebarVisible(false);
    setSidebarOpen(false);
  }, []);

  const handleSidebarOpen = useCallback(() => {
    setSidebarVisible(true);
    setSidebarOpen(true);
  }, []);

const handleMainClick = useCallback(() => {
  if (isMobile && sidebarVisible) { 
    setSidebarVisible(false);
    setSidebarOpen(false);
  }
}, [isMobile, sidebarVisible]);

  // Check if we're on mobile and update sidebar state accordingly
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Only set initial state when switching between mobile/desktop
      setSidebarOpen(prev => {
        if (mobile && prev) {
          return false;
        } else if (!mobile && !prev) {
          return true;
        }
        return prev;
      });
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []); // Only run on mount and cleanup

  const sidebarWidth = sidebarVisible ? (sidebarOpen ? SIDEBAR_OPEN_WIDTH : SIDEBAR_COLLAPSED_WIDTH) : 0;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="bg-gray-100 min-h-screen">
        <Navbar
          sidebarWidth={sidebarWidth}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={handleSidebarToggle}
          isMobile={isMobile}
          onSidebarOpen={handleSidebarOpen}
        />
        {sidebarVisible && (
          <Sidebar
            open={sidebarOpen}
            onClose={handleSidebarClose}
            isMobile={isMobile}
          />
        )}
        <main
          className="p-2 sm:p-2 bg-gray-100 transition-all duration-200"
          style={{
            marginLeft: isMobile ? 0 : sidebarWidth,
            paddingTop: '4rem',
            width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`
          }}
          onClick={handleMainClick}
        >
          {children}
        </main>
      </div>
    </>
  );
};

export default LayoutWrapper;
